/**
 * oraItaliana.js — l'unica fonte, nella schermata, di una data o di
 * un'ora italiana, e dell'istante che corrisponde a un orario scelto
 * sulla griglia dell'agenda.
 *
 * PERCHE' NON BASTA «TANTO IL BROWSER E' IN ITALIA»
 * Quasi sempre lo e'. Ma il fuso del browser lo decide la postazione,
 * non la clinica: un portatile tornato da un viaggio con il fuso
 * sbagliato, una macchina virtuale, un telefono in trasferta creano
 * appuntamenti all'ora sbagliata **senza nessun errore**. L'agenda di
 * un poliambulatorio e' in ora italiana per definizione: si dichiara.
 *
 * IL DIFETTO CHE HA FATTO NASCERE QUESTO FILE (23 settembre 2026)
 * Cliccando uno spazio della griglia il modulo mandava al server
 * `"2026-09-17T10:00"`, cioe' un orario SENZA fuso. Il server, che gira
 * in UTC, lo leggeva come le 10:00 UTC e lo salvava cosi': in agenda
 * l'appuntamento compariva alle **12:00**. Modifica e trascinamento
 * invece convertivano (`toISOString`), quindi nella stessa colonna
 * convivevano due convenzioni, e aprire «Modifica» e salvare senza
 * toccare niente spostava l'appuntamento indietro di due ore.
 * Misurato in produzione: **tutti e sette** gli appuntamenti creati da
 * una persona erano spostati.
 *
 * Il gemello sul server e' `backend/utils/oraItaliana.js`, con gli
 * stessi nomi.
 */

const FUSO = 'Europe/Rome';

const aIstante = (valore) => {
    if (valore instanceof Date) return valore;
    if (valore === undefined || valore === null || valore === '') return new Date();
    const d = new Date(valore);
    return isNaN(d) ? null : d;
};

/** «23/09/2026» e «10:45» in ora italiana. Stesso nome del server. */
export const momentoItaliano = (istante = new Date()) => {
    const d = aIstante(istante) || new Date();
    return {
        data: new Intl.DateTimeFormat('it-IT', {
            timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric',
        }).format(d),
        ora: new Intl.DateTimeFormat('it-IT', {
            timeZone: FUSO, hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(d),
    };
};

/** «23/09/2026» */
export const dataIta = (valore) => {
    if (!valore) return '-';
    const d = aIstante(valore);
    if (!d) return String(valore);
    return new Intl.DateTimeFormat('it-IT', {
        timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric',
    }).format(d);
};

/** «23/09/26, 10:45» — la stessa forma del piede dei PDF. */
export const dataOraIta = (valore) => {
    const d = aIstante(valore);
    if (!d) return String(valore);
    return new Intl.DateTimeFormat('it-IT', {
        timeZone: FUSO, day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d).replace(/,\s*/, ', ');
};

/** «10:45» */
export const oraIta = (valore) => momentoItaliano(valore).ora;

/** L'ora italiana come numero decimale: serve a posizionare sulla griglia. */
export const oraDecimaleIta = (valore) => {
    const [h, m] = oraIta(valore).split(':').map(Number);
    return h + m / 60;
};

/** «AAAA-MM-GG» del giorno italiano. */
export const oggiClinica = (istante = new Date()) => {
    const d = aIstante(istante) || new Date();
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(d);
};

/** «AAAA-MM» del mese italiano: il mese da cui parte una schermata. Mai
    toISOString().slice(0, 7), che fra mezzanotte e l'una (le due d'estate)
    del 1° da' il mese prima. */
export const meseClinica = (istante = new Date()) => oggiClinica(istante).slice(0, 7);

/** Il giorno italiano di un istante, «AAAA-MM-GG». */
export const giornoIta = (valore) => oggiClinica(aIstante(valore) || new Date());

/**
 * Primo e ultimo giorno del mese di `giorno` («AAAA-MM-GG», di serie oggi
 * nella clinica), spostato di `scarto` mesi. I conti si fanno con Date.UTC
 * e si leggono in UTC, quindi non dipendono dal fuso del browser (7/10/2026):
 * new Date(anno, mese, 1).toISOString() su una postazione in Italia dava la
 * mezzanotte italiana letta in UTC, cioe' il giorno prima - «Mese corrente»
 * andava dal 30 settembre al 30 ottobre.
 */
export const meseDi = (giorno = oggiClinica(), scarto = 0) => {
    const [a, m] = String(giorno).slice(0, 10).split('-').map(Number);
    const primo = new Date(Date.UTC(a, m - 1 + scarto, 1));
    const ultimo = new Date(Date.UTC(a, m + scarto, 0));
    return { dal: primo.toISOString().slice(0, 10), al: ultimo.toISOString().slice(0, 10) };
};

/**
 * Di quanto Roma e' avanti rispetto a UTC in quell'istante, in
 * millisecondi. Non e' una costante: d'estate sono due ore, d'inverno
 * una, e il giorno del cambio le due cose convivono.
 */
function scartoDiRoma(istante) {
    const p = Object.fromEntries(
        new Intl.DateTimeFormat('en-US', {
            timeZone: FUSO, hour12: false,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        }).formatToParts(istante).map((x) => [x.type, x.value])
    );
    const comeSeFosseUtc = Date.UTC(
        Number(p.year), Number(p.month) - 1, Number(p.day),
        Number(p.hour) % 24, Number(p.minute), Number(p.second)
    );
    return comeSeFosseUtc - istante.getTime();
}

/**
 * L'istante che corrisponde a un orario italiano scritto come
 * «AAAA-MM-GGTHH:MM» (la forma di `datetime-local` e quella che la
 * griglia compone cliccando).
 *
 * NON SI CERCA A TENTONI. La prima versione partiva da una stima e la
 * correggeva due volte con lo scarto del momento stimato. Funziona 363
 * giorni l'anno e il 25 ottobre **oscilla**: con le 01:30 si ferma sul
 * primo dei due passaggi, con le 02:30 sul secondo. Una funzione che
 * decide in base a dove e' partita non ha una regola, ha un caso.
 *
 * Qui si costruiscono i candidati - un istante per ognuno degli scarti
 * possibili di quel giorno - e si tiene solo quelli VERI: un istante
 * vale se, riportato in ora italiana, da' esattamente l'orario chiesto.
 *
 * I DUE GIORNI STRANI DELL'ANNO:
 *  - ultima domenica di ottobre: un'ora esiste DUE volte (dalle 03:00
 *    si torna alle 02:00). Restano due candidati veri, e si prende **il
 *    primo**, quello ancora in ora legale;
 *  - ultima domenica di marzo: un'ora NON esiste (dalle 02:00 si salta
 *    alle 03:00). Non resta nessun candidato vero, e si torna l'istante
 *    subito dopo il salto.
 * Nessuna delle due scelte e' piu' «giusta» dell'altra. Conta che sia
 * decisa e stabile, non che dipenda dall'orario di partenza: in clinica
 * a quell'ora non c'e' nessuno, ma chi legge fra sei mesi non deve
 * doverlo scoprire da solo. Le prove stanno in `oraItaliana.test.js`.
 */
export const istanteItaliano = (testo) => {
    const m = String(testo || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (!m) return null;
    const [, Y, M, G, h, min] = m.map(Number);
    const murale = Date.UTC(Y, M - 1, G, h, min);

    /* Gli scarti che Roma puo' avere in quel giro d'orologio: si
       guardano tre momenti attorno all'orario chiesto, non si scrivono
       «+1 e +2» a mano - se un giorno l'Europa toglie l'ora legale
       questa funzione continua a lavorare senza che nessuno la tocchi. */
    const scarti = new Set([0, 1, 2, -1].map(
        (ore) => scartoDiRoma(new Date(murale - ore * 3600000))));

    const veri = [...scarti]
        .map((s) => murale - s)
        .filter((t) => scartoDiRoma(new Date(t)) === murale - t)
        .sort((a, b) => a - b);

    if (veri.length) return new Date(veri[0]);
    /* L'ora che non esiste: subito dopo il salto. */
    return new Date(murale - scartoDiRoma(new Date(murale)));
};

/**
 * Il contrario: un istante nella forma che vuole `datetime-local`,
 * «AAAA-MM-GGTHH:MM», in ora italiana.
 *
 * `toISOString().slice(0,16)` darebbe l'ora UTC, e i metodi `getHours`
 * e compagnia danno l'ora del browser: nessuno dei due e' l'ora della
 * clinica.
 */
export const perCampoItaliano = (valore) => {
    const d = aIstante(valore);
    if (!d) return '';
    return `${giornoIta(d)}T${oraIta(d)}`;
};

export default momentoItaliano;
